import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { ChevronLeft, ChevronRight, FileDown, Users, Target, Banknote, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, CreditCard, ChevronDown, ChevronUp, Calendar, Package } from "lucide-react";
import { toast } from "sonner";
import LojaSelector from "@/components/LojaSelector";
import LoadingSpinner from "@/components/LoadingSpinner";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CHART_TOOLTIP_STYLE = { background: "#fff", border: "1px solid #E2E8F0", borderRadius: "6px", fontSize: 12 };
const BAR_RADIUS_TOP = [4, 4, 0, 0];
const BAR_RADIUS_RIGHT = [0, 4, 4, 0];
const COMPARATIVO_MARGIN = { left: 20 };

const MESES = ["", "Janeiro", "Fevereiro", "Marco", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];
const COLORS = ["#0F172A", "#334155", "#64748B", "#94A3B8", "#CBD5E1"];

const ExecutivoMensal = () => {
  const [mesAtual, setMesAtual] = useState(new Date().getMonth() + 1);
  const [anoAtual, setAnoAtual] = useState(new Date().getFullYear());
  const [dados, setDados] = useState(null);
  const [comparativo, setComparativo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lojaSelecionada, setLojaSelecionada] = useState("Todas as lojas");
  const [filtroPersonalizado, setFiltroPersonalizado] = useState(false);
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  
  // Estados para Repasses do Crediário
  const [mostrarRepasses, setMostrarRepasses] = useState(false);
  const [repassesData, setRepassesData] = useState(null);
  const [loadingRepasses, setLoadingRepasses] = useState(false);
  const [repassesExpandidos, setRepassesExpandidos] = useState({});

  const carregarDados = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filtroPersonalizado && dataInicio && dataFim) {
        params.data_inicio = dataInicio;
        params.data_fim = dataFim;
      } else {
        params.mes = mesAtual;
        params.ano = anoAtual;
      }
      if (lojaSelecionada !== "Todas as lojas") params.loja = lojaSelecionada;
      const [resExec, resComp] = await Promise.all([
        axios.get(`${API}/dashboard/executivo-mensal`, { params }),
        axios.get(`${API}/notificacoes/comparativo`, { params: { mes: mesAtual, ano: anoAtual } }),
      ]);
      setDados(resExec.data);
      setComparativo(resComp.data);
    } catch { toast.error("Erro ao carregar dados"); } finally { setLoading(false); }
  }, [mesAtual, anoAtual, lojaSelecionada, filtroPersonalizado, dataInicio, dataFim]);

  useEffect(() => { carregarDados(); }, [carregarDados]);

  // Carregar repasses do crediário
  const carregarRepasses = useCallback(async () => {
    try {
      setLoadingRepasses(true);
      const params = { mes: mesAtual, ano: anoAtual };
      if (lojaSelecionada !== "Todas as lojas") params.loja = lojaSelecionada;
      const res = await axios.get(`${API}/dashboard/repasses-crediario`, { params });
      setRepassesData(res.data);
    } catch { toast.error("Erro ao carregar repasses"); } finally { setLoadingRepasses(false); }
  }, [mesAtual, anoAtual, lojaSelecionada]);

  useEffect(() => {
    if (mostrarRepasses) carregarRepasses();
  }, [mostrarRepasses, carregarRepasses]);

  const toggleRepasseExpandido = (dia) => {
    setRepassesExpandidos(prev => ({ ...prev, [dia]: !prev[dia] }));
  };

  const navegarMes = (dir) => {
    let m = mesAtual + dir, a = anoAtual;
    if (m > 12) { m = 1; a++; } if (m < 1) { m = 12; a--; }
    setMesAtual(m); setAnoAtual(a);
  };

  const fmt = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

  const getMetaBorderColor = (pct) => {
    if (Number(pct) >= 100) return "border-l-emerald-500";
    if (Number(pct) >= 70) return "border-l-amber-500";
    return "border-l-red-500";
  };

  const getPctBadgeColor = (pct) => {
    if (pct >= 100) return "bg-emerald-100 text-emerald-700";
    if (pct >= 70) return "bg-amber-100 text-amber-700";
    return "bg-red-100 text-red-700";
  };

  const getCrescimentoColor = (pct) => {
    if (pct > 0) return "bg-emerald-100 text-emerald-700";
    if (pct < 0) return "bg-red-100 text-red-700";
    return "bg-slate-100 text-slate-600";
  };

  const getCrescimentoIcon = (pct) => {
    if (pct > 0) return <ArrowUpRight className="w-3 h-3" />;
    if (pct < 0) return <ArrowDownRight className="w-3 h-3" />;
    return null;
  };

  const getRepasseDiaColor = (dia) => {
    if (dia === 10) return "bg-blue-600";
    if (dia === 20) return "bg-green-600";
    return "bg-purple-600";
  };

  const exportarPDF = () => {
    if (!dados) return;
    const s = dados.stats;
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text(`Relatorio Executivo - ${MESES[mesAtual]} ${anoAtual}`, 14, 20);
    doc.setFontSize(9);
    doc.text(`Loja: ${lojaSelecionada}  |  Gerado: ${new Date().toLocaleDateString("pt-BR")}`, 14, 27);

    autoTable(doc, {
      startY: 33, head: [["Indicador", "Valor"]],
      body: [["Faturamento", fmt(s.total_vendas)], ["Receitas", String(s.total_receitas)], ["Ticket Medio", fmt(s.media_venda)]],
      theme: "grid", headStyles: { fillColor: [15, 23, 42] }, styles: { fontSize: 9 },
    });

    if (s.vendas_por_loja?.length > 0) {
      const y = doc.lastAutoTable?.finalY || 60;
      doc.setFontSize(11); doc.text("Desempenho por Loja", 14, y + 10);
      autoTable(doc, {
        startY: y + 14, head: [["Loja", "Vendas", "Faturamento", "Meta", "Ating."]],
        body: s.vendas_por_loja.map((l) => {
          const meta = dados.metas?.[l.loja] || 0;
          return [l.loja, String(l.quantidade), fmt(l.valor), fmt(meta), meta > 0 ? `${((l.valor / meta) * 100).toFixed(1)}%` : "-"];
        }),
        theme: "grid", headStyles: { fillColor: [15, 23, 42] }, styles: { fontSize: 9 },
      });
    }

    if (dados.avista_por_loja?.length > 0) {
      const y = doc.lastAutoTable?.finalY || 100;
      doc.setFontSize(11); doc.text("Vendas a Vista por Loja", 14, y + 10);
      autoTable(doc, {
        startY: y + 14, head: [["Loja", "A Vista", "Total", "% Valor", "% Qtd"]],
        body: dados.avista_por_loja.map((l) => [l.loja, fmt(l.avista_valor), fmt(l.total), `${l.pct_valor}%`, `${l.pct_qtd}%`]),
        theme: "grid", headStyles: { fillColor: [15, 23, 42] }, styles: { fontSize: 9 },
      });
    }

    if (dados.top_clientes?.length > 0) {
      const y = doc.lastAutoTable?.finalY || 140;
      doc.setFontSize(11); doc.text("Top 10 Clientes", 14, y + 10);
      autoTable(doc, {
        startY: y + 14, head: [["#", "Cliente", "Compras", "Total"]],
        body: dados.top_clientes.map((c, i) => [String(i + 1), c.nome, String(c.quantidade), fmt(c.valor)]),
        theme: "grid", headStyles: { fillColor: [15, 23, 42] }, styles: { fontSize: 9 },
      });
    }

    doc.save(`executivo_${MESES[mesAtual]}_${anoAtual}.pdf`);
    toast.success("PDF gerado!");
  };

  const lojas = dados?.stats?.vendas_por_loja || [];
  const pagData = dados?.stats?.vendas_por_forma_pagamento || [];
  const metaLoja = lojaSelecionada !== "Todas as lojas" ? (dados?.metas?.[lojaSelecionada] || 0) : 0;
  const totalMetas = Object.values(dados?.metas || {}).reduce((a, b) => a + b, 0);
  const metaRef = lojaSelecionada !== "Todas as lojas" ? metaLoja : totalMetas;
  const pctMeta = metaRef > 0 ? ((dados?.stats?.total_vendas || 0) / metaRef * 100).toFixed(1) : 0;

  return (
    <div className="space-y-6" data-testid="executivo-mensal-container">
      <div className="flex flex-wrap gap-4 items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Relatorio Executivo</h1>
          <p className="text-sm text-slate-500 mt-1">Visao consolidada para gestao</p>
        </div>
        <div className="flex gap-2 items-center">
          <LojaSelector lojaSelecionada={lojaSelecionada} onLojaChange={setLojaSelecionada} />
          <Button variant="outline" size="sm" onClick={exportarPDF} data-testid="btn-pdf-executivo">
            <FileDown className="w-3.5 h-3.5 mr-1.5" /> PDF
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        {!filtroPersonalizado ? (
          <>
            <Button variant="outline" size="icon" onClick={() => navegarMes(-1)} data-testid="btn-mes-anterior">
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <div className="bg-slate-900 text-white px-6 py-2 rounded-lg font-semibold text-lg min-w-[220px] text-center" data-testid="label-mes">
              {MESES[mesAtual]} {anoAtual}
            </div>
            <Button variant="outline" size="icon" onClick={() => navegarMes(1)} data-testid="btn-mes-proximo">
              <ChevronRight className="w-4 h-4" />
            </Button>
          </>
        ) : (
          <div className="flex items-center gap-2">
            <input type="date" value={dataInicio} onChange={(e) => setDataInicio(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white" data-testid="input-data-inicio" />
            <span className="text-slate-400 text-sm">ate</span>
            <input type="date" value={dataFim} onChange={(e) => setDataFim(e.target.value)}
              className="px-3 py-2 border border-slate-300 rounded-lg text-sm bg-white" data-testid="input-data-fim" />
          </div>
        )}
        <button onClick={() => {
          setFiltroPersonalizado(!filtroPersonalizado);
          if (!filtroPersonalizado) {
            const hoje = new Date();
            const inicio = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
            setDataInicio(inicio.toISOString().split('T')[0]);
            setDataFim(hoje.toISOString().split('T')[0]);
          }
        }}
          className={`px-3 py-2 text-xs font-medium rounded-lg border transition-colors ${
            filtroPersonalizado ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
          }`}
          data-testid="btn-filtro-personalizado">
          Personalizado
        </button>
      </div>

      {loading ? (
        <LoadingSpinner message="Carregando relatorio..." />
      ) : dados ? (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="kpis-executivo">
            <Card className="border-l-4 border-l-slate-900 bg-white" data-testid="kpi-faturamento">
              <CardContent className="pt-4 pb-3">
                <p className="text-xs text-slate-500 uppercase tracking-wider">Faturamento</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">{fmt(dados.stats.total_vendas)}</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-slate-600 bg-white">
              <CardContent className="pt-4 pb-3">
                <p className="text-xs text-slate-500 uppercase tracking-wider">Receitas</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">{dados.stats.total_receitas}</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-slate-400 bg-white">
              <CardContent className="pt-4 pb-3">
                <p className="text-xs text-slate-500 uppercase tracking-wider">Ticket Medio</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">{fmt(dados.stats.media_venda)}</p>
              </CardContent>
            </Card>
            <Card className={`border-l-4 bg-white ${getMetaBorderColor(pctMeta)}`}>
              <CardContent className="pt-4 pb-3">
                <p className="text-xs text-slate-500 uppercase tracking-wider">Meta</p>
                <p className="text-2xl font-bold text-slate-900 mt-1">{pctMeta}%</p>
              </CardContent>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Desempenho por Loja */}
            <Card className="bg-white border shadow-sm" data-testid="tabela-lojas">
              <CardHeader className="pb-2 border-b">
                <div className="flex items-center gap-2">
                  <Target className="w-4 h-4 text-slate-500" />
                  <CardTitle className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Desempenho por Loja</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="space-y-4">
                  {lojas.map((loja) => {
                    const meta = dados.metas?.[loja.loja] || 0;
                    const pct = meta > 0 ? (loja.valor / meta) * 100 : 0;
                    return (
                      <div key={loja.loja} className="space-y-1.5">
                        <div className="flex justify-between items-center">
                          <span className="text-sm font-semibold text-slate-800">{loja.loja}</span>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-500">{loja.quantidade} vendas</span>
                            <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${getPctBadgeColor(pct)}`}>
                              {meta > 0 ? `${pct.toFixed(0)}%` : "-"}
                            </span>
                          </div>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-slate-600">
                          <span>{fmt(loja.valor)}</span>
                          <span className="text-slate-300">/</span>
                          <span className="text-slate-400">Meta: {meta > 0 ? fmt(meta) : "N/D"}</span>
                        </div>
                        <Progress value={Math.min(pct, 100)} className="h-1.5" />
                      </div>
                    );
                  })}
                  {lojas.length === 0 && <p className="text-sm text-slate-400 text-center py-4">Sem dados</p>}
                </div>
              </CardContent>
            </Card>

            {/* Pie chart pagamentos */}
            {pagData.length > 0 && (
              <Card className="bg-white border shadow-sm" data-testid="pie-pagamentos">
                <CardHeader className="pb-2 border-b">
                  <CardTitle className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Distribuicao por Pagamento</CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie data={pagData} dataKey="valor" nameKey="forma_pagamento" cx="50%" cy="50%"
                        innerRadius={50} outerRadius={80} strokeWidth={2} stroke="#fff">
                        {pagData.map((entry, i) => <Cell key={entry.forma_pagamento} fill={COLORS[i % COLORS.length]} />)}
                      </Pie>
                      <Tooltip formatter={(v) => fmt(v)} />
                    </PieChart>
                  </ResponsiveContainer>
                  <div className="grid grid-cols-2 gap-2 mt-3">
                    {pagData.map((item) => (
                      <div key={item.forma_pagamento} className="flex items-center gap-2 text-xs">
                        <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: COLORS[pagData.indexOf(item) % COLORS.length] }} />
                        <span className="text-slate-600 truncate">{item.forma_pagamento}</span>
                        <span className="font-semibold text-slate-900 ml-auto">{fmt(item.valor)}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* % Vendas a Vista por Loja */}
          {dados.avista_por_loja?.length > 0 && (
            <Card className="bg-white border shadow-sm" data-testid="avista-por-loja">
              <CardHeader className="pb-2 border-b">
                <div className="flex items-center gap-2">
                  <Banknote className="w-4 h-4 text-slate-500" />
                  <CardTitle className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Vendas a Vista por Loja</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-left">
                      <th className="p-2.5 text-xs font-semibold text-slate-500 uppercase">Loja</th>
                      <th className="p-2.5 text-xs font-semibold text-slate-500 uppercase text-right">Valor a Vista</th>
                      <th className="p-2.5 text-xs font-semibold text-slate-500 uppercase text-center">% do Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dados.avista_por_loja.map((l) => (
                      <tr key={l.loja} className="border-b border-slate-50 hover:bg-slate-50">
                        <td className="p-2.5 font-medium text-slate-900">{l.loja}</td>
                        <td className="p-2.5 text-right text-slate-700">{fmt(l.avista_valor)}</td>
                        <td className="p-2.5 text-center">
                          <span className={`font-bold text-xs px-2 py-0.5 rounded ${l.pct_valor >= 50 ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                            {l.pct_valor}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-xs text-slate-400 mt-3 px-2.5">A vista = PIX, Debito, Dinheiro</p>
              </CardContent>
            </Card>
          )}

          {/* Peças Vendidas por Loja */}
          {dados.pecas_por_loja?.length > 0 && (
            <Card className="bg-white border shadow-sm" data-testid="pecas-por-loja">
              <CardHeader className="pb-2 border-b">
                <div className="flex items-center gap-2">
                  <Package className="w-4 h-4 text-slate-500" />
                  <CardTitle className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Peças Vendidas por Loja</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-4 space-y-4">
                {dados.pecas_por_loja.map((l) => (
                  <div key={l.loja} className="border border-slate-200 rounded-lg p-3" data-testid={`pecas-loja-${l.loja}`}>
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-semibold text-slate-900">{l.loja}</span>
                      <span className="text-xs font-bold bg-slate-900 text-white px-2.5 py-1 rounded-full">{l.total_pecas} peças</span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-center">
                      {[
                        { label: "Lentes", val: l.lentes },
                        { label: "Armação", val: l.armacao_total },
                        { label: "Óculos de Sol", val: l.oculos_total },
                        { label: "Acessórios", val: l.acessorios },
                        { label: "Serviços", val: l.servicos },
                      ].map((c) => (
                        <div key={c.label} className="bg-slate-50 rounded-md py-2">
                          <p className="text-lg font-bold text-slate-900">{c.val}</p>
                          <p className="text-[11px] text-slate-500 uppercase tracking-wide">{c.label}</p>
                        </div>
                      ))}
                    </div>
                    {(Object.keys(l.armacao_estilos || {}).length > 0 || Object.keys(l.oculos_estilos || {}).length > 0) && (
                      <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {Object.keys(l.armacao_estilos || {}).length > 0 && (
                          <div>
                            <p className="text-[11px] font-semibold text-slate-500 uppercase mb-1">Armação por estilo</p>
                            {Object.entries(l.armacao_estilos).map(([estilo, qtd]) => (
                              <div key={estilo} className="flex justify-between text-xs text-slate-600 py-0.5">
                                <span>{estilo}</span><span className="font-semibold">{qtd}</span>
                              </div>
                            ))}
                          </div>
                        )}
                        {Object.keys(l.oculos_estilos || {}).length > 0 && (
                          <div>
                            <p className="text-[11px] font-semibold text-slate-500 uppercase mb-1">Óculos de Sol por estilo</p>
                            {Object.entries(l.oculos_estilos).map(([estilo, qtd]) => (
                              <div key={estilo} className="flex justify-between text-xs text-slate-600 py-0.5">
                                <span>{estilo}</span><span className="font-semibold">{qtd}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Origem dos Clientes e Descontos por Loja */}
          {(dados.origem_ranking?.length > 0 || dados.desconto_por_loja?.length > 0) && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {dados.origem_ranking?.length > 0 && (
                <Card className="bg-white border shadow-sm" data-testid="origem-clientes">
                  <CardHeader className="pb-2 border-b">
                    <div className="flex items-center gap-2">
                      <Users className="w-4 h-4 text-slate-500" />
                      <CardTitle className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Origem dos Clientes</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-4 space-y-4">
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Ranking geral</p>
                      <div className="space-y-1.5">
                        {dados.origem_ranking.map((o, i) => (
                          <div key={o.origem} className="flex items-center justify-between text-sm p-2 rounded bg-slate-50" data-testid={`origem-rank-${i}`}>
                            <span className="text-slate-700"><span className="text-slate-400 font-mono mr-2">{i + 1}</span>{o.origem}</span>
                            <span className="font-semibold text-slate-900">{o.quantidade}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                    {dados.origem_por_loja?.length > 0 && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        {dados.origem_por_loja.map((l) => (
                          <div key={l.loja} className="border border-slate-200 rounded-lg p-3" data-testid={`origem-loja-${l.loja}`}>
                            <div className="flex items-center justify-between mb-2">
                              <span className="font-semibold text-slate-900 text-sm">{l.loja}</span>
                              <span className="text-xs font-bold bg-slate-900 text-white px-2 py-0.5 rounded-full">{l.total}</span>
                            </div>
                            {l.origens.map((o) => (
                              <div key={o.origem} className="flex justify-between text-xs text-slate-600 py-0.5">
                                <span>{o.origem}</span><span className="font-semibold">{o.quantidade}</span>
                              </div>
                            ))}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
              {dados.desconto_por_loja?.length > 0 && (
                <Card className="bg-white border shadow-sm" data-testid="descontos-loja">
                  <CardHeader className="pb-2 border-b">
                    <div className="flex items-center gap-2">
                      <TrendingDown className="w-4 h-4 text-slate-500" />
                      <CardTitle className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Descontos Concedidos</CardTitle>
                    </div>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <div className="p-3 rounded-lg bg-amber-50 border border-amber-200 mb-3">
                      <p className="text-xs text-amber-600 font-medium uppercase">Desconto Total no Periodo</p>
                      <p className="text-2xl font-bold text-amber-900 mt-1" data-testid="desconto-total">{fmt(dados.desconto_total)}</p>
                    </div>
                    <div className="space-y-1.5">
                      {dados.desconto_por_loja.map((l) => (
                        <div key={l.loja} className="flex items-center justify-between text-sm p-2 rounded bg-slate-50" data-testid={`desconto-loja-${l.loja}`}>
                          <span className="text-slate-700">{l.loja}</span>
                          <span className="font-semibold text-amber-700">{fmt(l.total_desconto)}</span>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Ticket Médio por Tipo de Lente */}
          {(dados.stats?.ticket_lente_simples?.quantidade > 0 || dados.stats?.ticket_lente_multifocal?.quantidade > 0) && (
            <Card className="bg-white border shadow-sm" data-testid="ticket-lentes">
              <CardHeader className="pb-2 border-b">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-slate-500" />
                  <CardTitle className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Ticket Medio por Tipo de Lente</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                {/* Resumo Geral */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="p-3 rounded-lg bg-blue-50 border border-blue-200">
                    <p className="text-xs text-blue-600 font-medium uppercase">Lente Visao Simples</p>
                    <p className="text-xl font-bold text-blue-900 mt-1">{fmt(dados.stats?.ticket_lente_simples?.media || 0)}</p>
                    <p className="text-xs text-blue-500 mt-0.5">{dados.stats?.ticket_lente_simples?.quantidade || 0} vendas</p>
                  </div>
                  <div className="p-3 rounded-lg bg-purple-50 border border-purple-200">
                    <p className="text-xs text-purple-600 font-medium uppercase">Lente Multifocal</p>
                    <p className="text-xl font-bold text-purple-900 mt-1">{fmt(dados.stats?.ticket_lente_multifocal?.media || 0)}</p>
                    <p className="text-xs text-purple-500 mt-0.5">{dados.stats?.ticket_lente_multifocal?.quantidade || 0} vendas</p>
                  </div>
                </div>
                
                {/* Detalhamento por Loja */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Visão Simples por Loja */}
                  {dados.stats?.ticket_lente_simples_por_loja?.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Visao Simples por Loja</p>
                      <div className="space-y-1.5">
                        {dados.stats.ticket_lente_simples_por_loja.map((l) => (
                          <div key={l.loja} className="flex items-center justify-between text-sm p-2 rounded bg-slate-50">
                            <span className="text-slate-700">{l.loja}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-slate-400">{l.quantidade} vendas</span>
                              <span className="font-semibold text-blue-700">{fmt(l.media)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  
                  {/* Multifocal por Loja */}
                  {dados.stats?.ticket_lente_multifocal_por_loja?.length > 0 && (
                    <div>
                      <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Multifocal por Loja</p>
                      <div className="space-y-1.5">
                        {dados.stats.ticket_lente_multifocal_por_loja.map((l) => (
                          <div key={l.loja} className="flex items-center justify-between text-sm p-2 rounded bg-slate-50">
                            <span className="text-slate-700">{l.loja}</span>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-slate-400">{l.quantidade} vendas</span>
                              <span className="font-semibold text-purple-700">{fmt(l.media)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Evolucao Semanal */}
          {dados.vendas_por_semana?.length > 0 && (
            <Card className="bg-white border shadow-sm" data-testid="chart-semanas">
              <CardHeader className="pb-2 border-b">
                <CardTitle className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Evolucao Semanal</CardTitle>
              </CardHeader>
              <CardContent className="pt-4">
                <ResponsiveContainer width="100%" height={220}>
                  <BarChart data={dados.vendas_por_semana}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="semana" stroke="#94A3B8" fontSize={11} tickFormatter={(v) => `S${v}`} />
                    <YAxis stroke="#94A3B8" fontSize={11} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                    <Tooltip formatter={(v) => fmt(v)} labelFormatter={(l) => `Semana ${l}`}
                      contentStyle={CHART_TOOLTIP_STYLE} />
                    <Bar dataKey="valor" fill="#0F172A" radius={BAR_RADIUS_TOP} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Top Clientes */}
          {dados.top_clientes?.length > 0 && (
            <Card className="bg-white border shadow-sm" data-testid="top-clientes">
              <CardHeader className="pb-2 border-b">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-slate-500" />
                  <CardTitle className="text-sm font-semibold text-slate-700 uppercase tracking-wider">Top 10 Clientes</CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 text-left">
                      <th className="p-2.5 text-xs font-semibold text-slate-500 uppercase">#</th>
                      <th className="p-2.5 text-xs font-semibold text-slate-500 uppercase">Cliente</th>
                      <th className="p-2.5 text-xs font-semibold text-slate-500 uppercase text-center">Compras</th>
                      <th className="p-2.5 text-xs font-semibold text-slate-500 uppercase text-right">Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dados.top_clientes.map((c, i) => (
                      <tr key={c.nome || i} className="border-b border-slate-50 hover:bg-slate-50">
                        <td className="p-2.5 text-slate-400 font-mono">{i + 1}</td>
                        <td className="p-2.5 text-slate-900 font-medium">{c.nome}</td>
                        <td className="p-2.5 text-center text-slate-600">{c.quantidade}</td>
                        <td className="p-2.5 text-right font-semibold text-slate-900">{fmt(c.valor)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}

          {/* Comparativo Mensal entre Lojas */}
          {comparativo?.comparativo?.length > 0 && (
            <Card className="bg-white border shadow-sm" data-testid="comparativo-lojas">
              <CardHeader className="pb-2 border-b">
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-slate-500" />
                  <CardTitle className="text-sm font-semibold text-slate-700 uppercase tracking-wider">
                    Comparativo vs Mes Anterior ({MESES[comparativo.mes_anterior]} {comparativo.ano_anterior})
                  </CardTitle>
                </div>
              </CardHeader>
              <CardContent className="pt-4">
                {/* Chart */}
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={comparativo.comparativo} layout="vertical" margin={COMPARATIVO_MARGIN}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis type="number" stroke="#94A3B8" fontSize={11} tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`} />
                    <YAxis dataKey="loja" type="category" stroke="#94A3B8" fontSize={11} width={100} />
                    <Tooltip formatter={(v) => fmt(v)} contentStyle={CHART_TOOLTIP_STYLE} />
                    <Legend fontSize={11} />
                    <Bar dataKey="atual_valor" name={MESES[mesAtual]} fill="#0F172A" radius={BAR_RADIUS_RIGHT} barSize={14} />
                    <Bar dataKey="anterior_valor" name={MESES[comparativo.mes_anterior]} fill="#CBD5E1" radius={BAR_RADIUS_RIGHT} barSize={14} />
                  </BarChart>
                </ResponsiveContainer>

                {/* Table */}
                <table className="w-full text-sm mt-4">
                  <thead>
                    <tr className="border-b border-slate-200 text-left">
                      <th className="p-2.5 text-xs font-semibold text-slate-500 uppercase">Loja</th>
                      <th className="p-2.5 text-xs font-semibold text-slate-500 uppercase text-right">Atual</th>
                      <th className="p-2.5 text-xs font-semibold text-slate-500 uppercase text-right">Anterior</th>
                      <th className="p-2.5 text-xs font-semibold text-slate-500 uppercase text-right">Diferenca</th>
                      <th className="p-2.5 text-xs font-semibold text-slate-500 uppercase text-center">Crescimento</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparativo.comparativo.map((c) => (
                      <tr key={c.loja} className="border-b border-slate-50 hover:bg-slate-50">
                        <td className="p-2.5 font-medium text-slate-900">{c.loja}</td>
                        <td className="p-2.5 text-right text-slate-700">{fmt(c.atual_valor)}</td>
                        <td className="p-2.5 text-right text-slate-400">{fmt(c.anterior_valor)}</td>
                        <td className={`p-2.5 text-right font-medium ${c.diferenca >= 0 ? "text-emerald-600" : "text-red-600"}`}>
                          {c.diferenca >= 0 ? "+" : ""}{fmt(c.diferenca)}
                        </td>
                        <td className="p-2.5 text-center">
                          <span className={`inline-flex items-center gap-0.5 text-xs font-bold px-2 py-0.5 rounded ${getCrescimentoColor(c.crescimento_pct)}`}>
                            {getCrescimentoIcon(c.crescimento_pct)}
                            {c.crescimento_pct > 0 ? "+" : ""}{c.crescimento_pct}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          )}

          {/* Repasses do Crediário */}
          <Card className="bg-white border shadow-sm" data-testid="repasses-crediario">
            <CardHeader className="pb-2 border-b">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-purple-600" />
                  <CardTitle className="text-base font-semibold text-slate-900">Repasses do Crediario</CardTitle>
                </div>
                <Button 
                  variant={mostrarRepasses ? "default" : "outline"} 
                  size="sm" 
                  onClick={() => setMostrarRepasses(!mostrarRepasses)}
                  data-testid="btn-toggle-repasses"
                >
                  {mostrarRepasses ? "Ocultar" : "Ver Repasses"}
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-1">
                Conferencia dos pagamentos do crediario nos dias 10, 20 e 30 de {MESES[mesAtual]}
              </p>
            </CardHeader>
            
            {mostrarRepasses && (
              <CardContent className="pt-4">
                {loadingRepasses ? (
                  <LoadingSpinner message="Carregando repasses..." />
                ) : repassesData ? (
                  <div className="space-y-4">
                    {/* Resumo */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div className="p-3 rounded-lg bg-purple-50 border border-purple-200">
                        <p className="text-xs text-purple-600 font-medium">Total Geral</p>
                        <p className="text-lg font-bold text-purple-900 mt-1">{fmt(repassesData.total_geral)}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                        <p className="text-xs text-slate-500 font-medium">Repasse Dia 10</p>
                        <p className="text-lg font-bold text-slate-900 mt-1">{fmt(repassesData.repasses[0]?.total || 0)}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                        <p className="text-xs text-slate-500 font-medium">Repasse Dia 20</p>
                        <p className="text-lg font-bold text-slate-900 mt-1">{fmt(repassesData.repasses[1]?.total || 0)}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-slate-50 border border-slate-200">
                        <p className="text-xs text-slate-500 font-medium">Repasse Dia 30</p>
                        <p className="text-lg font-bold text-slate-900 mt-1">{fmt(repassesData.repasses[2]?.total || 0)}</p>
                      </div>
                    </div>

                    {/* Detalhamento por repasse */}
                    <div className="space-y-3">
                      {repassesData.repasses.map((repasse) => (
                        <div key={repasse.dia_repasse} className="border border-slate-200 rounded-lg overflow-hidden">
                          {/* Header do Repasse */}
                          <button
                            onClick={() => toggleRepasseExpandido(repasse.dia_repasse)}
                            className="w-full flex items-center justify-between p-3 bg-slate-50 hover:bg-slate-100 transition-colors"
                            data-testid={`btn-repasse-${repasse.dia_repasse}`}
                          >
                            <div className="flex items-center gap-3">
                              <span className={`text-sm font-bold text-white px-3 py-1 rounded ${getRepasseDiaColor(repasse.dia_repasse)}`}>
                                Dia {repasse.dia_repasse}
                              </span>
                              <div className="text-left">
                                <p className="text-sm font-medium text-slate-700">{repasse.periodo}</p>
                                <p className="text-xs text-slate-400">{repasse.quantidade} vendas no crediario</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              <span className="font-bold text-slate-900 text-lg">{fmt(repasse.total)}</span>
                              {repassesExpandidos[repasse.dia_repasse] ? (
                                <ChevronUp className="w-5 h-5 text-slate-400" />
                              ) : (
                                <ChevronDown className="w-5 h-5 text-slate-400" />
                              )}
                            </div>
                          </button>
                          
                          {/* Detalhes do Repasse (expandível) */}
                          {repassesExpandidos[repasse.dia_repasse] && repasse.vendas?.length > 0 && (
                            <div className="border-t border-slate-200">
                              <table className="w-full text-sm">
                                <thead>
                                  <tr className="bg-slate-100 text-left">
                                    <th className="p-2 text-xs font-semibold text-slate-500">Data</th>
                                    <th className="p-2 text-xs font-semibold text-slate-500">OS</th>
                                    <th className="p-2 text-xs font-semibold text-slate-500">Cliente</th>
                                    <th className="p-2 text-xs font-semibold text-slate-500">Loja</th>
                                    <th className="p-2 text-xs font-semibold text-slate-500 text-right">Valor Crediario</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {repasse.vendas.map((v, j) => (
                                    <tr key={`${v.os || "s"}-${v.data || ""}-${j}`} className="border-b border-slate-50 hover:bg-slate-50">
                                      <td className="p-2 text-slate-600">
                                        {new Date(v.data + "T12:00:00").toLocaleDateString("pt-BR")}
                                      </td>
                                      <td className="p-2 text-slate-600">{v.os || "-"}</td>
                                      <td className="p-2 text-slate-900 font-medium">{v.cliente || "-"}</td>
                                      <td className="p-2 text-slate-600">{v.loja || "-"}</td>
                                      <td className="p-2 text-right font-semibold text-purple-700">{fmt(v.valor_crediario)}</td>
                                    </tr>
                                  ))}
                                </tbody>
                                <tfoot>
                                  <tr className="bg-slate-50 font-bold border-t border-slate-200">
                                    <td colSpan="4" className="p-2 text-right text-slate-700">Total do Repasse:</td>
                                    <td className="p-2 text-right text-purple-700">{fmt(repasse.total)}</td>
                                  </tr>
                                </tfoot>
                              </table>
                            </div>
                          )}
                          
                          {/* Mensagem quando não há vendas */}
                          {repassesExpandidos[repasse.dia_repasse] && repasse.vendas?.length === 0 && (
                            <div className="p-4 text-center text-slate-500 text-sm border-t border-slate-200">
                              Nenhuma venda no crediario neste periodo
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {/* Legenda */}
                    <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <p className="text-xs text-amber-700 font-medium flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        Regras de Corte:
                      </p>
                      <ul className="text-xs text-amber-600 mt-1.5 space-y-0.5 ml-6">
                        <li>• <strong>Dia 10:</strong> Vendas de 06 a 15 do mes anterior</li>
                        <li>• <strong>Dia 20:</strong> Vendas de 16 a 25 do mes anterior</li>
                        <li>• <strong>Dia 30:</strong> Vendas de 26 do mes anterior a 05 do mes atual</li>
                      </ul>
                    </div>
                  </div>
                ) : null}
              </CardContent>
            )}
          </Card>
        </>
      ) : null}
    </div>
  );
};

export default ExecutivoMensal;
